import type { OverlayItem } from "../api/posterTemplateService";

/**
 * V37 (2026-05-18) — 给详情页临时挂载 logo / QR 算默认落点。
 *
 * 三个维度决定:
 * - canvasAspect: 画布宽/高(横屏 > 1,竖屏 < 1)
 * - assetAspect: logo 真实长宽比('SQUARE' | 'WIDE' | 'TALL'),QR 永远 1:1
 * - kind: 'logo' → 左上角,'qr' → 右下角(行业惯例)
 *
 * 输出 normalized (0..1) 的 {x, y, w, h},直接喂 OverlayItem。
 * 落点会自动维持 logo 物理长宽比(用 canvasAspect 换算 h)。
 */

const PADDING = 0.04;

const ASSET_ASPECT_MAP: Record<string, number> = {
  SQUARE: 1,
  WIDE: 3,
  TALL: 0.5,
};

/**
 * 给 logo 算默认落点 — 左上角。
 *
 * @param canvasAspect 画布 w / h(海报 ratio,如 1:1 → 1,9:16 → 0.5625,16:9 → 1.78)
 * @param assetAspectKey 来自 BrandAssets.logos[].aspect,null/undefined 当 SQUARE
 */
export function pickDefaultLogoPosition(
  canvasAspect: number,
  assetAspectKey: string | null | undefined
): OverlayItem {
  const w = canvasAspect < 1 ? 0.18 : 0.14;
  const assetAspect = ASSET_ASPECT_MAP[assetAspectKey ?? "SQUARE"] ?? 1;
  // physical_h_px = (w * canvas_w_px) / assetAspect
  // h_norm        = physical_h_px / canvas_h_px = (w * canvasAspect) / assetAspect
  const h = clamp((w * canvasAspect) / assetAspect, 0.06, 0.3);
  return { x: PADDING, y: PADDING, w, h };
}

/**
 * 给 QR 算默认落点 — 右下角。QR 物理上永远方形。
 */
export function pickDefaultQrPosition(canvasAspect: number): OverlayItem {
  const w = canvasAspect < 1 ? 0.18 : 0.14;
  // QR 物理方形:h_norm = w * (canvas_w_px / canvas_h_px) = w * canvasAspect
  const h = w * canvasAspect;
  return {
    x: 1 - PADDING - w,
    y: 1 - PADDING - h,
    w,
    h,
  };
}

function clamp(v: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, v));
}
